'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CURRENT_ENV } from 'utils/constants';

import type { ReactNode } from 'react';

interface ConsentContextType {
  hasMarketingConsent: boolean;
  hasPerformanceConsent: boolean;
  consentGroups: string[];
  isLoaded: boolean;
}

// Default Context Values
const ConsentContext = createContext<ConsentContextType>({
  hasMarketingConsent: false,
  hasPerformanceConsent: false,
  consentGroups: [],
  isLoaded: false,
});

// Consent Provider
//   checks OneTrust
//   normalizes it
//   keeps state update with consent changes

export const ConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consentGroups, setConsentGroups] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadedRef = useRef(false); // stops once OneTrust is detected
  const lastLogKeyRef = useRef<string | null>(null); // prevent duplicates

  useEffect(() => {
    const updateConsent = () => {
      // One Trust group IDs (e.g. ["C0001"])
      const raw = window.OnetrustActiveGroups;

      // loaded even if raw === ""
      const loaded = raw != null || window.OneTrust != null;

      const groups = (raw ?? '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      setConsentGroups(groups);

      if (loaded && !loadedRef.current) {
        loadedRef.current = true;
        setIsLoaded(true);
      }

      // dev-only: log when consent becomes known or changes
      if (CURRENT_ENV !== 'prod') {
        const hasMarketing = groups.includes('C0004');
        const hasPerformance = groups.includes('C0002');
        const hasFunctional = groups.includes('C0003');

        let onetrustStatus = 'WAITING FOR ONETRUST';
        if (loaded && groups.length > 0) {
          onetrustStatus = 'LOADED (groups set)';
        }
        if (loaded && groups.length === 0) {
          onetrustStatus = 'LOADED (no groups)';
        }

        let hubspotStatus = 'SCRIPT NOT LOADED';
        if (!loaded) {
          hubspotStatus = 'WAITING FOR ONETRUST';
        }
        if (loaded && !hasMarketing) {
          hubspotStatus = 'BLOCKED (no marketing consent)';
        }
        if (loaded && hasMarketing && (document.getElementById('hs-script-loader') || Array.isArray(window._hsq))) {
          hubspotStatus = 'LOADED';
        }

        let datadogStatus = 'SCRIPT NOT LOADED';
        if (!loaded) {
          datadogStatus = 'WAITING FOR ONETRUST';
        }
        if (loaded && !hasPerformance) {
          datadogStatus = 'BLOCKED (no performance consent)';
        }
        if (loaded && hasPerformance && window.DD_RUM && typeof window.DD_RUM.getInternalContext === 'function') {
          datadogStatus = 'INITIALIZED';
        }

        let userwayStatus = 'SCRIPT NOT LOADED';
        if (!loaded) {
          userwayStatus = 'WAITING FOR ONETRUST';
        }
        if (loaded && !hasFunctional) {
          userwayStatus = 'BLOCKED (no functional consent)';
        }
        if (loaded && hasFunctional && document.getElementById('userway-widget')) {
          userwayStatus = 'LOADED';
        }

        const logkey = [onetrustStatus, groups.join(''), hubspotStatus, datadogStatus, userwayStatus].join('|');

        if (lastLogKeyRef.current !== logkey) {
          lastLogKeyRef.current = logkey;

          console.log('[Consent]', {
            oneTrust: onetrustStatus,
            groups: groups.join(',') || '(none)',
            hubspot: hubspotStatus,
            datadog: datadogStatus,
            userway: userwayStatus,
          });
        }
      }
    };

    updateConsent();

    const events = ['OneTrustGroupsUpdated', 'consent.onetrust'] as const;

    // Listen for consent changes
    events.forEach(event => {
      window.addEventListener(event, updateConsent);
      document.addEventListener(event, updateConsent);
    });

    // Poll as backup (in case OneTrust loads after this component)
    const pollInterval = setInterval(() => {
      if (!isLoaded) {
        updateConsent();
      }
    }, 500);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateConsent);
        document.removeEventListener(event, updateConsent);
      });
      window.clearInterval(pollInterval);
      clearInterval(pollInterval);
    };
  }, [isLoaded]);

  const ConsentValues = useMemo(() => {
    const hasMarketingConsent = consentGroups.includes('C0004');
    const hasPerformanceConsent = consentGroups.includes('C0002');

    return { hasMarketingConsent, hasPerformanceConsent, consentGroups, isLoaded };
  }, [consentGroups, isLoaded]);

  return <ConsentContext.Provider value={ConsentValues}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);

// Type declarations
declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    OneTrust?: any;
  }
}
